import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { format, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import TransactionModal from '../components/TransactionModal';

const { FiPlus, FiTrendingUp, FiTrendingDown, FiPieChart, FiDollarSign, FiEdit, FiTrash2, FiTarget } = FiIcons;

const CATEGORIES = [
  'Lebensmittel', 'Wohnen', 'Transport', 'Versicherung', 
  'Freizeit', 'Sparen', 'Sonstiges', 'Gehalt', 'Geschenk', 
  'Restaurant', 'Kleidung', 'Gesundheit'
];

export default function BudgetTracker() {
  const { state, dispatch } = useFamilyContext();
  const { financeTransactions, financeBudgets } = state;
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null); // Which category is being edited

  // Filter for current month
  const currentDate = new Date();
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const monthlyTransactions = useMemo(() => {
    return financeTransactions.filter(t => {
      const d = parseISO(t.date);
      return isWithinInterval(d, { start: monthStart, end: monthEnd });
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [financeTransactions]);

  const stats = useMemo(() => {
    let income = 0;
    let expense = 0;
    monthlyTransactions.forEach(t => {
      if (t.type === 'income') income += Number(t.amount);
      else expense += Number(t.amount);
    });
    return { income, expense, balance: income - expense };
  }, [monthlyTransactions]);

  // Budget Calculations
  const budgetProgress = useMemo(() => {
    const progress = {};
    // Initialize
    CATEGORIES.forEach(cat => progress[cat] = { spent: 0, limit: 0 });
    
    // Sum Expenses
    monthlyTransactions.filter(t => t.type === 'expense').forEach(t => {
      if (!progress[t.category]) progress[t.category] = { spent: 0, limit: 0 };
      progress[t.category].spent += Number(t.amount);
    });

    // Add Limits
    financeBudgets.forEach(b => {
      if (!progress[b.category]) progress[b.category] = { spent: 0, limit: 0 };
      progress[b.category].limit = Number(b.limit_amount);
      progress[b.category].id = b.id; // Store ID for updates
    });

    return progress;
  }, [monthlyTransactions, financeBudgets]);

  const handleSaveTransaction = (data) => {
    if (editingTransaction) {
      dispatch({ type: 'UPDATE_TRANSACTION', payload: { ...editingTransaction, ...data } });
      setEditingTransaction(null);
    } else {
      dispatch({ type: 'ADD_TRANSACTION', payload: data });
    }
  };

  const handleDeleteTransaction = (id) => {
    if (confirm('Eintrag löschen?')) {
      dispatch({ type: 'DELETE_TRANSACTION', payload: id });
    }
  };

  const handleSaveBudget = (category, amount) => {
     const existing = financeBudgets.find(b => b.category === category);
     if (existing) {
         if (amount <= 0) {
             dispatch({ type: 'DELETE_BUDGET', payload: existing.id });
         } else {
             dispatch({ type: 'UPDATE_BUDGET', payload: { ...existing, limit_amount: amount } });
         }
     } else if (amount > 0) {
         dispatch({ type: 'ADD_BUDGET', payload: { category, limit_amount: amount, period: 'monthly' } });
     }
     setEditingBudget(null);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Haushaltsbuch</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Finanzübersicht für {format(currentDate, 'MMMM yyyy', { locale: de })}</p>
        </div>
        <button
          onClick={() => { setEditingTransaction(null); setShowModal(true); }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md mt-4 sm:mt-0"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Neuer Eintrag
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
           <div className="flex items-center justify-between">
               <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                   <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-green-600 dark:text-green-400" />
               </div>
               <span className="text-sm text-gray-500 dark:text-gray-400">Einnahmen</span>
           </div>
           <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">+{stats.income.toFixed(2)} €</h3>
        </motion.div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
           <div className="flex items-center justify-between">
               <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                   <SafeIcon icon={FiTrendingDown} className="w-6 h-6 text-red-600 dark:text-red-400" />
               </div>
               <span className="text-sm text-gray-500 dark:text-gray-400">Ausgaben</span>
           </div>
           <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-4">-{stats.expense.toFixed(2)} €</h3>
        </motion.div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 relative overflow-hidden">
           <div className="absolute top-0 right-0 p-4 opacity-10">
               <SafeIcon icon={FiPieChart} className="w-24 h-24 text-blue-600" />
           </div>
           <div className="flex items-center justify-between relative z-10">
               <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                   <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
               </div>
               <span className="text-sm text-gray-500 dark:text-gray-400">Bilanz</span>
           </div>
           <h3 className={`text-2xl font-bold mt-4 relative z-10 ${stats.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
               {stats.balance >= 0 ? '+' : ''}{stats.balance.toFixed(2)} €
           </h3>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Budget Limits Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                  <SafeIcon icon={FiTarget} className="w-5 h-5 mr-2 text-purple-600" />
                  Budgets
              </h2>
              <div className="space-y-4">
                  {CATEGORIES.map(cat => {
                      const data = budgetProgress[cat] || { spent: 0, limit: 0 };
                      const hasActivity = data.spent > 0 || data.limit > 0;
                      if (!hasActivity && editingBudget !== cat) return null;

                      const percent = data.limit > 0 ? Math.min((data.spent / data.limit) * 100, 100) : 0;
                      const isOver = data.spent > data.limit && data.limit > 0;

                      return (
                          <div key={cat} className="group">
                              <div className="flex justify-between items-end mb-1">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{cat}</span>
                                  {editingBudget === cat ? (
                                      <div className="flex items-center space-x-2">
                                          <input 
                                            type="number" 
                                            defaultValue={data.limit} 
                                            className="w-20 text-xs border rounded px-1 py-0.5 dark:bg-gray-700 dark:text-white"
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveBudget(cat, parseFloat(e.target.value));
                                            }}
                                            onBlur={(e) => handleSaveBudget(cat, parseFloat(e.target.value))}
                                          />
                                          <span className="text-xs text-gray-500">€</span>
                                      </div>
                                  ) : (
                                      <div className="flex items-center space-x-2">
                                          <span className={`text-sm ${isOver ? 'text-red-600 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                                              {data.spent.toFixed(0)} / {data.limit > 0 ? data.limit : '∞'} €
                                          </span>
                                          <button 
                                            onClick={() => setEditingBudget(cat)}
                                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity"
                                          >
                                              <SafeIcon icon={FiEdit} className="w-3 h-3" />
                                          </button>
                                      </div>
                                  )}
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                                  <div 
                                    className={`h-2.5 rounded-full ${isOver ? 'bg-red-500' : 'bg-blue-500'}`} 
                                    style={{ width: `${percent}%` }}
                                  ></div>
                              </div>
                          </div>
                      );
                  })}
                  
                  {/* Add Budget Button if not all categories shown */}
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      <select 
                        className="text-sm text-blue-600 bg-transparent hover:underline cursor-pointer focus:outline-none"
                        onChange={(e) => setEditingBudget(e.target.value)}
                        value=""
                      >
                          <option value="" disabled>+ Budget hinzufügen...</option>
                          {CATEGORIES.filter(c => !budgetProgress[c]?.limit && !budgetProgress[c]?.spent).map(c => (
                              <option key={c} value={c}>{c}</option>
                          ))}
                      </select>
                  </div>
              </div>
          </div>

          {/* Recent Transactions List */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Verlauf</h2>
              <div className="space-y-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                  {monthlyTransactions.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">Noch keine Einträge diesen Monat.</p>
                  ) : (
                      monthlyTransactions.map(t => (
                          <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group">
                              <div className="flex items-center space-x-3">
                                  <div className={`w-2 h-10 rounded-full ${t.type === 'income' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                  <div>
                                      <p className="font-medium text-gray-900 dark:text-white">{t.category}</p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">{format(parseISO(t.date), 'dd.MM.')} • {t.description || 'Ohne Beschreibung'}</p>
                                  </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                  <span className={`font-bold ${t.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                                      {t.type === 'income' ? '+' : '-'}{Number(t.amount).toFixed(2)} €
                                  </span>
                                  <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button onClick={() => { setEditingTransaction(t); setShowModal(true); }} className="p-1 text-gray-400 hover:text-blue-600">
                                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => handleDeleteTransaction(t.id)} className="p-1 text-gray-400 hover:text-red-600">
                                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                      </button>
                                  </div>
                              </div>
                          </div>
                      ))
                  )}
              </div>
          </div>
      </div>

      {showModal && (
        <TransactionModal
          transaction={editingTransaction}
          onClose={() => setShowModal(false)}
          onSave={handleSaveTransaction}
        />
      )}
    </div>
  );
}